// src/pages/LoginPage.tsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FirebaseError } from "firebase/app";
import { useAuth } from "../contexts/AuthContext";
import AuthLayout from "./AuthLayout";
import { getUserRole, db, auth } from "../services/firebase";
import { doc, getDoc } from "firebase/firestore";
import StyledLoadingSpinner from "../components/ui/StyledLoadingSpinner";

const validateEmail = (email: string) =>
  /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);

const validatePassword = (password: string) => {
  /* /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*]).{8,}$/.test(password); */
  if (password && password.trim().length > 0) {
    return true;
  } else {
    return false;
  }
};

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const navigate = useNavigate();
  const { login, logout } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  const checkAdminRole = async () => {
    if (user) {
      const userDoc = await getDoc(doc(db, "users", user.uid));
      if (userDoc.exists() && userDoc.data().role === "admin") {
        return true;
      }
    }
    return false;
  };

  useEffect(() => {
    checkAdminRole().then((isAdmin) => {
      if (isAdmin) {
        navigate("/admin");
      }
    });
  }, [user]);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    setEmailError(
      validateEmail(e.target.value) ? "" : "Invalid email address."
    );
    if (submitError) setSubmitError("");
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    setPasswordError(
      validatePassword(e.target.value) ? "" : "Password is required."
    );
    if (submitError) setSubmitError("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateEmail(email)) {
      setEmailError("Invalid email address.");
      return;
    }

    if (!validatePassword(password)) {
      setPasswordError(
        "Password must be at least 8 characters, include uppercase, lowercase, a number, and a special character."
      );
      return;
    }

    try {
      setIsLoading(true);
      setIsSubmitting(true);
      const userCredential = await login(email, password);
      const user = userCredential?.user;
      if (user) {
        const userDoc = await getDoc(doc(db, "users", user.uid));
        if (userDoc.exists() && userDoc.data().role !== "admin") {
          await auth.signOut();
          await logout();
          setIsSubmitting(false);
          setSubmitError("You do not have admin access.");
          return;
        } else {
          await user.reload();
          const role = await getUserRole(user.uid);

          if (role === "admin") {
            navigate("/admin");
          } else {
            setIsSubmitting(false);
            setSubmitError("You do not have admin access.");
            await logout();
          }
        }
      }
    } catch (err: unknown) {
      setIsSubmitting(false);
      const firebaseError = err as FirebaseError;
      console.log("Login error:", firebaseError);
      switch (firebaseError.code) {
        case "auth/user-not-found":
          setSubmitError("No user found with this email.");
          break;
        case "auth/wrong-password":
          setSubmitError("Incorrect password.");
          break;
        case "auth/invalid-credential":
          setSubmitError("Invalid email or password.");
          break;
        default:
          setSubmitError("Failed to log in. Please try again.");
          break;
      }
    } finally {
      setIsLoading(false);
    }
  };

  const isFormValid = validateEmail(email) && validatePassword(password);

  if (isLoading) {
    return <StyledLoadingSpinner />;
  }

  return (
    <AuthLayout title="Admin Sign In">
      <form onSubmit={handleSubmit}>
        <div className="formGroup">
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={handleEmailChange}
            required
            autoFocus
          />
          {emailError && <div className="errorText">{emailError}</div>}
        </div>

        <div className="formGroup">
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={handlePasswordChange}
            required
          />
          {passwordError && <div className="errorText">{passwordError}</div>}
        </div>
        <button
          type="submit"
          className="btnStyle"
          disabled={!isFormValid || isSubmitting}
        >
          Continue
        </button>
        {submitError && <div className="blockError">{submitError}</div>}
      </form>
    </AuthLayout>
  );
};

export default LoginPage;
